// Image Block Component
// Display images with captions - editing happens in modal
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon } from 'lucide-react';
import api from '../../services/api';

const ImageBlock = ({ content, padding, backgroundColor, gridWidth, gridHeight, onUpdate, onDelete }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [url, setUrl] = useState(content?.url || '');
  const [alt, setAlt] = useState(content?.alt || '');
  const [caption, setCaption] = useState(content?.caption || '');
  const [editPadding, setEditPadding] = useState(padding || 'medium');
  const [editBackgroundColor, setEditBackgroundColor] = useState(backgroundColor || '');
  const [editGridWidth, setEditGridWidth] = useState(gridWidth || 12);
  const [editGridHeight, setEditGridHeight] = useState(gridHeight || 2);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.request('/upload/single', {
        method: 'POST',
        body: formData
      });

      const imageUrl = response.file?.url || response.url;
      setUrl(imageUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    onUpdate({
      content: {
        url,
        alt,
        caption
      },
      padding: editPadding,
      backgroundColor: editBackgroundColor || null,
      gridWidth: editGridWidth,
      gridHeight: editGridHeight
    });
    setShowEditModal(false);
  };

  const handleCancel = () => {
    setUrl(content?.url || '');
    setAlt(content?.alt || '');
    setCaption(content?.caption || '');
    setEditPadding(padding || 'medium');
    setEditBackgroundColor(backgroundColor || '');
    setEditGridWidth(gridWidth || 12);
    setEditGridHeight(gridHeight || 2);
    setShowEditModal(false);
  };

  const openEditModal = () => {
    // Reset to current values when opening
    setUrl(content?.url || '');
    setAlt(content?.alt || '');
    setCaption(content?.caption || '');
    setEditPadding(padding || 'medium');
    setEditBackgroundColor(backgroundColor || '');
    setEditGridWidth(gridWidth || 12);
    setEditGridHeight(gridHeight || 2);
    setShowEditModal(true);
  };

  const getPaddingClass = () => {
    switch (padding) {
      case 'none': return 'p-0';
      case 'small': return 'p-2';
      case 'large': return 'p-6';
      default: return 'p-4';
    }
  };

  // Render the edit modal (same for empty and existing images)
  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">
            Edit Image
          </h3>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                Padding
              </label>
              <select
                value={editPadding}
                onChange={(e) => setEditPadding(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              >
                <option value="none">None</option>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                Background Color
              </label>
              <input
                type="color"
                value={editBackgroundColor || '#ffffff'}
                onChange={(e) => setEditBackgroundColor(e.target.value)}
                className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              />
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Upload Image
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
              {url ? (
                <div className="space-y-4">
                  <img src={url} alt={alt} className="max-w-full h-auto max-h-48 mx-auto rounded" />
                  <div className="flex justify-center gap-2">
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
                      Change Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                    <button
                      onClick={() => setUrl('')}
                      className="px-4 py-2 text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer block text-center">
                  <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {uploading ? 'Uploading...' : 'Click to upload an image'}
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Alt Text */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Alt Text (for accessibility)
            </label>
            <input
              type="text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              placeholder="Description of the image"
            />
          </div>

          {/* Caption */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Caption (optional)
            </label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              placeholder="Image caption"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8]"
            disabled={!url}
          >
            Update Image
          </button>
        </div>
      </div>
    </div>
  );

  // If no image yet, show placeholder with edit prompt
  if (!content?.url) {
    return (
      <>
        {/* Hidden edit trigger for PageBuilder */}
        <button
          data-edit-trigger
          onClick={openEditModal}
          className="hidden"
          aria-hidden="true"
        />
        <div
          className={`${getPaddingClass()} h-full flex items-center justify-center`}
          style={{ backgroundColor }}
        >
          <div className="text-center">
            <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Use edit button to add image</p>
          </div>
        </div>

        {showEditModal && createPortal(renderEditModal(), document.body)}
      </>
    );
  }

  // Display image - edit/delete handled by standardized block controls
  return (
    <>
      {/* Hidden edit trigger for PageBuilder */}
      <button
        data-edit-trigger
        onClick={openEditModal}
        className="hidden"
        aria-hidden="true"
      />
      <div
        className={`${getPaddingClass()} h-full`}
        style={{ backgroundColor }}
      >
        <div className="h-full flex flex-col">
          <div className="flex-1">
            <img
              src={content.url}
              alt={content.alt || ''}
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
          {content.caption && (
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-2 italic">
              {content.caption}
            </p>
          )}
        </div>
      </div>

      {showEditModal && createPortal(renderEditModal(), document.body)}
    </>
  );
};

export default ImageBlock;
